import { useEffect, useState } from 'react';
import EventRegistration, {
    EVENT_REGISTRATION_STATUSES,
    EVENT_PAYMENT_STATUSES,
    EventRegistrationStatus,
    EventPaymentStatus,
    MessengerApp,
} from '@/models/event-registration';
import { useRegistration, useUpdateRegistration, useDeleteRegistration, RegistrationUpdatePayload, formatEventStart } from '@/services/events';
import FormModal from '@/shared/components/form-modal';
import { MaterialFormContainer, MaterialFormOption, MaterialTextField } from '@/shared/components/form/MaterialFormElements';
import { formatDate } from '@/utils/helpers';
import { darkTextDim, errorBannerStyle } from '@/shared/dark-inline-styles';

interface RegistrationModalProps {
    isOpen: boolean;
    selectedRegistrationId?: string;
    toggle: () => void;
}

const STATUS_LABELS: Record<EventRegistrationStatus, string> = {
    confirmed: 'Confirmed',
    waitlisted: 'Waitlisted',
    cancelled: 'Cancelled',
};

const MESSENGER_LABELS: Record<MessengerApp, string> = {
    whatsapp: 'WhatsApp',
    telegram: 'Telegram',
    both: 'WhatsApp / Telegram',
};

const PAYMENT_STATUS_LABELS: Record<EventPaymentStatus, string> = {
    pending: 'Pending',
    verified: 'Verified',
    rejected: 'Rejected',
};

/** e.g. 1500000 → "1,500,000 Toman"; 0 / free → "Free". */
const formatToman = (amount: number): string => (amount > 0 ? `${new Intl.NumberFormat('en-US').format(amount)} Toman` : 'Free');

interface RegistrationForm {
    status: EventRegistrationStatus;
    paymentStatus: EventPaymentStatus;
    adminNote: string;
}

const labelStyle: React.CSSProperties = { fontSize: 11, textTransform: 'uppercase', letterSpacing: 1, color: darkTextDim, margin: '0 0 2px' };
const valueStyle: React.CSSProperties = { fontSize: 14, margin: '0 0 10px', whiteSpace: 'pre-line' };

const Field = ({ label, children }: { label: string; children: React.ReactNode }) => (
    <div>
        <p style={labelStyle}>{label}</p>
        <p style={valueStyle}>{children}</p>
    </div>
);

const RegistrationModal = ({ isOpen, toggle, selectedRegistrationId }: RegistrationModalProps) => {
    const { data: regRes, isLoading } = useRegistration(selectedRegistrationId as string, isOpen);
    const { mutateAsync: updateRegistration, isPending: isUpdating } = useUpdateRegistration();
    const { mutateAsync: deleteRegistration, isPending: isDeleting } = useDeleteRegistration();
    const reg: EventRegistration | undefined = regRes?.data ?? undefined;

    const [form, setForm] = useState<RegistrationForm>({ status: 'confirmed', paymentStatus: 'pending', adminNote: '' });
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!isOpen) return;
        if (reg) {
            setForm({ status: reg.status, paymentStatus: reg.paymentStatus, adminNote: reg.adminNote ?? '' });
        }
        setError(null);
    }, [isOpen, reg]);

    useEffect(() => {
        if (!isOpen) {
            setForm({ status: 'confirmed', paymentStatus: 'pending', adminNote: '' });
            setError(null);
        }
    }, [isOpen]);

    const set = <K extends keyof RegistrationForm>(key: K, value: RegistrationForm[K]) => setForm((f) => ({ ...f, [key]: value }));

    const handleSave = async () => {
        if (!selectedRegistrationId) return;
        const payload: RegistrationUpdatePayload = {
            status: form.status,
            paymentStatus: form.paymentStatus,
            // Send the trimmed string (not undefined) so CLEARING the note persists —
            // undefined would be dropped by axios and leave the old note in place.
            adminNote: form.adminNote.trim(),
        };
        try {
            // callApi never throws on API errors — inspect result.status.
            const result = await updateRegistration({ registrationId: selectedRegistrationId, ...payload });
            if (result.status === 'error') {
                setError(result.error?.message ?? 'Failed to update the registration.');
                return;
            }
            toggle();
        } catch (e) {
            console.error('Failed to update registration:', e);
            setError('Failed to update the registration. Check the console for details.');
        }
    };

    const handleDelete = async () => {
        if (!selectedRegistrationId) return;
        try {
            const result = await deleteRegistration({ registrationId: selectedRegistrationId });
            if (result.status === 'error') {
                setError(result.error?.message ?? 'Failed to delete the registration.');
                return;
            }
            toggle();
        } catch (e) {
            console.error('Failed to delete registration:', e);
            setError('Failed to delete the registration. Check the console for details.');
        }
    };

    return (
        <FormModal
            isOpen={isOpen}
            showDelete={!!selectedRegistrationId}
            title={reg ? reg.fullName : 'Registration'}
            body={
                <MaterialFormContainer onSubmit={handleSave}>
                    {error && <div style={errorBannerStyle}>{error}</div>}

                    {reg && (
                        <>
                            {/* ── Event ────────────────────────────────────── */}
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, textAlign: 'left', marginBottom: 8 }}>
                                <Field label="Event">{reg.eventTitle}</Field>
                                <Field label="When">{formatEventStart(reg.eventStartsAt)}</Field>
                                <Field label="Registered">{formatDate(reg.created_at)}</Field>
                                <Field label="Current status">{STATUS_LABELS[reg.status]}</Field>
                            </div>

                            {/* ── Contact ──────────────────────────────────── */}
                            <p style={{ ...labelStyle, marginTop: 8 }}>Contact</p>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, textAlign: 'left', marginBottom: 8 }}>
                                <Field label="Email">{reg.email}</Field>
                                <Field label="Phone">
                                    {reg.phone} <span style={{ color: '#999' }}>({MESSENGER_LABELS[reg.messengerApp] ?? reg.messengerApp})</span>
                                </Field>
                                <Field label="Instagram">{reg.instagram || '—'}</Field>
                                <Field label="Locale">{reg.locale || '—'}</Field>
                            </div>

                            {/* ── About ────────────────────────────────────── */}
                            <p style={{ ...labelStyle, marginTop: 8 }}>About</p>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, textAlign: 'left', marginBottom: 8 }}>
                                <Field label="Age">{reg.age}</Field>
                                <Field label="Filming consent">{reg.filmingConsent ? 'Yes' : 'No'}</Field>
                                <Field label="Has mat">{reg.hasMat ? 'Yes' : 'No'}</Field>
                                <Field label="Has headphones">{reg.hasHeadphones ? 'Yes' : 'No'}</Field>
                                <Field label="Education">{reg.education || '—'}</Field>
                                <Field label="Profession">{reg.profession || '—'}</Field>
                                <div style={{ gridColumn: '1 / -1' }}>
                                    <Field label="Health conditions">{reg.healthConditions || '—'}</Field>
                                </div>
                                <Field label="How they heard">{reg.referralMethod || '—'}</Field>
                                <Field label="Introduced by">{reg.referrerName || '—'}</Field>
                            </div>

                            {/* ── Payment ──────────────────────────────────── */}
                            <p style={{ ...labelStyle, marginTop: 8 }}>Payment</p>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, textAlign: 'left', marginBottom: 8 }}>
                                <Field label="Amount due">{formatToman(reg.amountDue ?? 0)}</Field>
                                <Field label="Promo code">{reg.appliedPromoCode || '—'}</Field>
                                <div style={{ gridColumn: '1 / -1' }}>
                                    <Field label="Receipt">
                                        {reg.receiptUrl ? (
                                            <a
                                                href={reg.receiptUrl}
                                                target="_blank"
                                                rel="noreferrer"
                                                style={{ color: '#00c6a9', textDecoration: 'underline' }}
                                            >
                                                View receipt ↗
                                            </a>
                                        ) : (reg.amountDue ?? 0) > 0 ? (
                                            'No receipt uploaded'
                                        ) : (
                                            '— (free)'
                                        )}
                                    </Field>
                                </div>
                            </div>

                            {/* ── Manage (editable) ────────────────────────── */}
                            <p style={{ margin: '12px 0 4px', fontWeight: 600, textAlign: 'left' }}>Manage</p>
                            <MaterialTextField
                                name="select"
                                select
                                label="Status"
                                value={form.status}
                                onChange={(e) => set('status', e.target.value as EventRegistrationStatus)}
                            >
                                {EVENT_REGISTRATION_STATUSES.map((s) => (
                                    <MaterialFormOption key={s} value={s}>
                                        {STATUS_LABELS[s]}
                                    </MaterialFormOption>
                                ))}
                            </MaterialTextField>
                            <p style={{ margin: '-6px 0 10px', fontSize: 12, color: '#999999' }}>
                                Only <strong>Confirmed</strong> registrations hold a seat. Cancelling frees a seat; re-confirming re-claims one
                                (refused if the event is full). Cancelling also emails the registrant.
                            </p>
                            <MaterialTextField
                                name="select"
                                select
                                label="Payment status"
                                value={form.paymentStatus}
                                onChange={(e) => set('paymentStatus', e.target.value as EventPaymentStatus)}
                            >
                                {EVENT_PAYMENT_STATUSES.map((s) => (
                                    <MaterialFormOption key={s} value={s}>
                                        {PAYMENT_STATUS_LABELS[s]}
                                    </MaterialFormOption>
                                ))}
                            </MaterialTextField>
                            <p style={{ margin: '-6px 0 10px', fontSize: 12, color: '#999999' }}>
                                Verify the uploaded receipt, then set this. It does not affect the seat.
                            </p>
                            <MaterialTextField
                                label="Internal note (not shown to the registrant)"
                                multiline
                                value={form.adminNote}
                                onChange={(e) => set('adminNote', e.target.value)}
                            />
                        </>
                    )}
                </MaterialFormContainer>
            }
            toggle={toggle}
            handleSave={handleSave}
            handleDelete={handleDelete}
            loading={isLoading || isUpdating || isDeleting}
        />
    );
};

export default RegistrationModal;
